import { Component, effect, inject, OnDestroy, untracked, viewChildren } from '@angular/core';
import { FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonCard,
  IonContent,
  IonHeader,
  IonItem,
  IonText,
  IonTitle,
  IonToolbar,
  ModalController,
  NavController,
} from '@ionic/angular/standalone';
import { VxErrorBarComponent } from '@libs/core/lib/error-view/error-bar.component';
import { NgEventBus } from '@libs/core/lib/event-bus/ng-event-bus';
import { VxBackButtonComponent } from '@libs/core/lib/form-controls/mtjr-back-button.component';
import { VxSubmitButtonComponent } from '@libs/core/lib/form-controls/mtjr-submit-button.component';
import { VxTextFieldComponent } from '@libs/core/lib/form-controls/mtjr-text-field.component';
import { errorState } from '@libs/core/lib/state/async-states';
import { Failure, LocalFailure } from '@libs/core/lib/state/failure';
import { AppAlerts } from '@libs/core/lib/utils/app-alerts';
import { VxAttachmentFieldComponent } from '@libs/mtjr/shared/attachment/attachment-field.component';
import { withVendorCreate } from '@libs/mtjr/shared/generated/entities/vendor/vendor-store-features';
import { vendorCreateUtils } from '@libs/mtjr/shared/generated/forms';
import { UserStore } from '@libs/mtjr/shared/user/user.store';
import { patchState, signalStore, withMethods } from '@ngrx/signals';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

export const CreateVendorStore = signalStore(
  withVendorCreate(),
  withMethods((store) => ({
    setFailure(failure: Failure) {
      patchState(store, {
        vendorCreateState: errorState(failure),
      });
    },
  })),
);

@Component({
  selector: 'mav-vendor-create',
  templateUrl: './vendor-create.page.html',
  imports: [
    IonContent,
    IonBackButton,
    IonButtons,
    IonToolbar,
    IonHeader,
    ReactiveFormsModule,
    FormsModule,
    VxSubmitButtonComponent,
    IonTitle,
    VxErrorBarComponent,
    VxBackButtonComponent,
    TranslatePipe,
    VxAttachmentFieldComponent,
    IonCard,
    IonItem,
    IonText,
    IonButton,
    RouterLink,
    VxTextFieldComponent,
  ],
  providers: [CreateVendorStore],
})
export class VendorCreateComponent implements OnDestroy {
  createStore = inject(CreateVendorStore);
  createUtils = vendorCreateUtils(inject(NonNullableFormBuilder));
  form = this.createUtils.form;
  navController = inject(NavController);
  eventBus = inject(NgEventBus);
  alerts = inject(AppAlerts);
  modalCtr = inject(ModalController);
  attachments = viewChildren(VxAttachmentFieldComponent);
  routeSub?: Subscription;
  userStore = inject(UserStore);
  route = inject(ActivatedRoute);
  router = inject(Router);

  ionViewWillLeave() {
    this.modalCtr.dismiss();
  }

  constructor() {
    this.userStore.fetchUserIfNeeded();
    effect(() => {
      const currentState = this.createStore.vendorCreateState();
      if (currentState.success) {
        untracked(() => {
          this.navController.navigateBack(['/main', 'vendors', currentState.data.id]);
          this.eventBus.cast('vendors:fetch');
          this.alerts.successSnackbarTR('VENDORS.VENDOR_CREATED_SUCCESS');
        });
      }
    });

    this.routeSub = this.route.queryParams.subscribe((params) => {
      const selectedAddrId = params['selectedAddrId'];
      if (selectedAddrId) {
        const address = this.userStore.addresses().find((addr) => addr.id === selectedAddrId);
        if (address) {
          this.createUtils.form.patchValue({
            addressLabel: address.label,
            addressStreet: address.street,
            addressInternal: address.internalAddress,
            addressPhone: address.phone,
            addressPhone2: address.phone2,
            addressLat: address.latitude,
            addressLng: address.longitude,
          });
          this.createUtils.form.markAsDirty();
        }
        this.router.navigate([], {
          queryParams: { selectedAddrId: null },
          relativeTo: this.route,
          replaceUrl: true,
          queryParamsHandling: 'merge',
        });
      }
    });
  }

  submit() {
    if (this.form.invalid) {
      this.createUtils.form.markAllAsTouched();
      return;
    }

    const result = this.createUtils.parse();
    if (!result.success) {
      this.createUtils.form.markAllAsTouched();
      this.createStore.setFailure(new LocalFailure());
      return;
    }

    if (this.attachments().some((a) => a.hasPendingUploads())) {
      this.alerts.dangerSnackbarTR('COMMON.ATTACHMENTS_UPLOAD_IN_PROGRESS');
      return;
    }

    this.createStore.vendorCreate(result.data);
  }

  ngOnDestroy() {
    this.routeSub?.unsubscribe();
  }
}
